import type { LegalDoc } from '../types';

// Transcribed 1:1 from "07 - Aviso de Derechos de Privacidad Estatales.pdf"
// (MEC Vault/legal/app-legal). The Section 2 table is reconstructed from the
// PDF layout (pdftotext scrambles multi-column tables).
export const privacidadEstatal: LegalDoc = {
  slug: '/privacidad-estatal',
  title: 'Aviso de Derechos de Privacidad Estatales',
  version: '1.0',
  updated: 'Julio de 2026',
  entity: 'Mi Envío Cuba',
  seoDescription:
    'Derechos de privacidad para residentes de California, Florida, Virginia, Colorado, Connecticut y Utah: acceso, eliminación, corrección, portabilidad y cómo ejercerlos.',
  closing: 'Fin del Aviso de Derechos de Privacidad Estatales.',
  sections: [
    {
      id: 'seccion-1',
      number: '1',
      title: 'Alcance de este Aviso',
      blocks: [
        {
          t: 'p',
          text: 'Este Aviso complementa nuestra Política de Privacidad (v1.0) y describe los derechos específicos de los residentes de ciertos estados de los Estados Unidos que han promulgado leyes integrales de privacidad del consumidor. Si usted reside en uno de esos estados, este Aviso le aplica además de los derechos generales descritos en la Política de Privacidad.',
        },
        { t: 'p', text: '**Estados actualmente cubiertos por este Aviso:**' },
        {
          t: 'ul',
          items: [
            '**California** — California Consumer Privacy Act (“CCPA”), enmendada por la California Privacy Rights Act (“CPRA”).',
            '**Florida** — Florida Digital Bill of Rights (“FDBR”), Fla. Stat. § 501.702 y siguientes.',
            '**Virginia** — Virginia Consumer Data Protection Act (“VCDPA”).',
            '**Colorado** — Colorado Privacy Act (“CPA”).',
            '**Connecticut** — Connecticut Data Privacy Act (“CTDPA”).',
            '**Utah** — Utah Consumer Privacy Act (“UCPA”).',
          ],
        },
        {
          t: 'p',
          text: 'Otros estados pueden agregarse a esta lista a medida que promulguen leyes similares. Si su estado no aparece pero cree que su ley local le confiere derechos, escríbanos y evaluaremos su solicitud de buena fe.',
        },
        { t: 'h3', text: '1.1 Rol de MEC como plataforma de software' },
        {
          t: 'p',
          text: 'Mi Envío Cuba es una plataforma tecnológica operada por MEC LLC que conecta al Usuario con proveedores independientes de servicios en el corredor Estados Unidos — Cuba (agencias de envío, comerciantes cubanos, transitarias, tiendas externas). Respecto de sus derechos de privacidad estatales:',
        },
        {
          t: 'ul',
          items: [
            '**MEC actúa como controlador** de los datos que recopila y procesa como operador de la Plataforma (información de cuenta, historial de transacciones a nivel de la Plataforma, datos técnicos y de seguridad, datos de cumplimiento regulatorio).',
            '**MEC actúa como procesador o facilitador** de los datos que las Agencias, los Comerciantes u otros proveedores gestionan a través de sus propias herramientas (Mi Dashboard, Mi Admin, Mi Restaurante, Mi Cargo). Respecto de esos datos, la Agencia o el Comerciante es el responsable primario.',
          ],
        },
        {
          t: 'p',
          text: 'Sus derechos bajo la ley estatal aplican plenamente frente a MEC en los datos que MEC controla. Cuando su solicitud se refiera a datos bajo el control primario de una Agencia o Comerciante, coordinamos con dicha parte conforme a la Sección 5 de este Aviso.',
        },
      ],
    },
    {
      id: 'seccion-2',
      number: '2',
      title: 'Categorías de información personal que recopilamos',
      blocks: [
        {
          t: 'p',
          text: 'En los últimos 12 meses, MEC ha recopilado las siguientes categorías de información personal, según definidas en la CCPA/CPRA. Para el desglose completo por fuente, propósito y destinatario, consulte la Política de Privacidad (Secciones 2, 3 y 4).',
        },
        {
          t: 'table',
          head: ['Categoría CCPA', '¿La recopilamos?', 'Ejemplos'],
          rows: [
            [
              'Identificadores',
              'Sí',
              'Nombre, correo, teléfono, dirección, dirección IP, identificador de dispositivo',
            ],
            [
              'Categorías de la Sección 1798.80 de la CCPA',
              'Sí',
              'Número de identificación gubernamental, información de pago',
            ],
            [
              'Características de clasificaciones protegidas',
              'Limitado',
              'Nacionalidad, cuando sea necesaria para verificación de identidad',
            ],
            ['Información comercial', 'Sí', 'Historial de transacciones, envíos, pedidos'],
            ['Información biométrica', 'No', '—'],
            [
              'Información de actividad en internet o red',
              'Sí',
              'Uso de la Aplicación, eventos, errores',
            ],
            [
              'Información de geolocalización',
              'Sí',
              'Ubicación GPS de dispositivo al momento de entrega (Mi Admin, Mi Restaurante)',
            ],
            ['Información sensorial', 'Sí', 'Fotografías de comprobante de entrega'],
            [
              'Información profesional o laboral',
              'Limitado',
              'En Mi Dashboard, para personal de agencias',
            ],
            ['Información educativa', 'No', '—'],
            ['Inferencias', 'Limitado', 'Preferencias de servicios inferidas del uso'],
          ],
        },
      ],
    },
    {
      id: 'seccion-3',
      number: '3',
      title: 'Categorías sensibles de información personal (CPRA)',
      blocks: [
        {
          t: 'p',
          text: 'Bajo la CPRA de California, ciertas categorías se consideran información personal sensible. MEC recopila las siguientes categorías sensibles:',
        },
        {
          t: 'ul',
          items: [
            'Números de identificación emitidos por el gobierno.',
            'Información de cuenta de pago (procesada por nuestro procesador externo).',
            'Información de geolocalización precisa (en Mi Admin y Mi Restaurante, al momento de la entrega).',
          ],
        },
        {
          t: 'p',
          text: '**MEC no utiliza información personal sensible con propósitos distintos a los descritos en la Política de Privacidad.** Como residente de California, usted tiene derecho a limitar el uso de su información personal sensible; escríbanos para ejercer este derecho.',
        },
      ],
    },
    {
      id: 'seccion-4',
      number: '4',
      title: 'Sus derechos',
      blocks: [
        {
          t: 'p',
          text: 'Dependiendo de su estado de residencia, usted podrá tener los siguientes derechos:',
        },
        { t: 'h3', text: '4.1 Derecho a saber / de acceso' },
        { t: 'p', text: 'Usted puede solicitar que MEC le informe:' },
        {
          t: 'ul',
          items: [
            'Las categorías de información personal que hemos recopilado sobre usted.',
            'Las fuentes de esa información.',
            'Los propósitos comerciales o de negocio para los que la recopilamos.',
            'Las categorías de terceros con quienes la hemos compartido.',
            'Las piezas específicas de información personal que hemos recopilado sobre usted.',
          ],
        },
        { t: 'h3', text: '4.2 Derecho a eliminar' },
        {
          t: 'p',
          text: 'Usted puede solicitar que MEC elimine información personal que hemos recopilado, sujeto a excepciones aplicables (por ejemplo, retención legal obligatoria, respuesta a solicitudes legales, ejercicio de derechos legales, entre otras).',
        },
        { t: 'h3', text: '4.3 Derecho a corregir' },
        { t: 'p', text: 'Usted puede solicitar que corrijamos información personal inexacta.' },
        { t: 'h3', text: '4.4 Derecho a portabilidad' },
        {
          t: 'p',
          text: 'Usted puede solicitar que le enviemos su información personal en un formato de uso común y legible por máquina (por ejemplo, JSON, CSV).',
        },
        {
          t: 'h3',
          text: '4.5 Derecho a optar por no participar en la “venta” o “compartición” de información personal',
        },
        {
          t: 'p',
          text: '**MEC no vende su información personal** en el sentido tradicional. MEC tampoco “comparte” su información personal para publicidad conductual entre sitios (cross-context behavioral advertising).',
        },
        {
          t: 'p',
          text: 'Si esto cambia, MEC actualizará este Aviso y proporcionará el mecanismo de exclusión requerido por la ley aplicable.',
        },
        { t: 'h3', text: '4.6 Derecho a limitar el uso de información sensible (California)' },
        {
          t: 'p',
          text: 'Como residente de California, puede solicitar que limitemos el uso y divulgación de su información personal sensible al necesario para prestar los Servicios que usted solicita.',
        },
        { t: 'h3', text: '4.7 Derecho a no discriminación' },
        {
          t: 'p',
          text: 'MEC no discriminará contra usted por ejercer cualquiera de estos derechos. No le negaremos servicios, cobraremos precios diferentes, ni proporcionaremos un nivel de calidad distinto por ejercer sus derechos.',
        },
        { t: 'h3', text: '4.8 Derecho a apelar (Virginia, Colorado, Connecticut, Florida)' },
        {
          t: 'p',
          text: 'Si MEC deniega su solicitud, usted tiene el derecho a apelar la decisión escribiéndonos dentro del plazo establecido por la ley aplicable de su estado.',
        },
      ],
    },
    {
      id: 'seccion-5',
      number: '5',
      title: 'Cómo ejercer sus derechos',
      blocks: [
        { t: 'p', text: 'Para ejercer cualquiera de estos derechos, escríbanos:' },
        {
          t: 'ul',
          items: [
            '**Correo:** support@mienviocuba.com',
            '**Formulario de solicitud dentro de la Aplicación:** Ajustes → Privacidad → Solicitud de derechos.',
          ],
        },
        {
          t: 'p',
          text: 'Le pediremos información razonable para verificar su identidad antes de responder, y solo utilizaremos esa información con el propósito de verificar la solicitud.',
        },
        {
          t: 'p',
          text: '**Plazo de respuesta:** típicamente 30 a 45 días desde la recepción de la solicitud verificada. En casos complejos, MEC podrá extender el plazo por un período adicional razonable, previa notificación a usted.',
        },
        {
          t: 'p',
          text: '**Enrutamiento de solicitudes cuando el dato esté bajo control primario de un tercero.** Si su solicitud se refiere a datos que están bajo el control primario de una Agencia o de un Comerciante (por ejemplo, el registro de sus transacciones dentro de una agencia específica, o el historial de compras con un Comerciante particular), MEC podrá dirigir su solicitud a la parte correspondiente. Se lo notificaremos cuando así lo hagamos e indicaremos el canal de contacto de la parte responsable, para que usted pueda continuar ejerciendo sus derechos frente a esa parte.',
        },
        {
          t: 'p',
          text: '**Agentes autorizados (California):** puede designar por escrito a un agente autorizado para presentar solicitudes en su nombre. MEC solicitará prueba de la designación y podrá pedir directamente al consumidor la verificación de identidad.',
        },
      ],
    },
    {
      id: 'seccion-6',
      number: '6',
      title: 'Métricas anuales de solicitudes (CCPA)',
      blocks: [
        {
          t: 'p',
          text: 'MEC publicará anualmente en su sitio web, según corresponda por umbrales de la ley aplicable, las métricas agregadas de solicitudes de derechos recibidas, procesadas, denegadas, y los tiempos promedio de respuesta.',
        },
      ],
    },
    {
      id: 'seccion-7',
      number: '7',
      title: 'Menores',
      blocks: [
        {
          t: 'p',
          text: 'Los Servicios están disponibles solo para personas mayores de 18 años. MEC no recopila conscientemente información personal de menores de 16 años. Si descubrimos haber recopilado información personal de un menor de 16 sin consentimiento parental verificable, la eliminaremos con prontitud.',
        },
        {
          t: 'p',
          text: 'Bajo la CCPA/CPRA, MEC no vende ni comparte información personal de menores de 16 años.',
        },
      ],
    },
    {
      id: 'seccion-8',
      number: '8',
      title: 'Categorías de terceros con quienes compartimos información',
      blocks: [
        {
          t: 'p',
          text: 'MEC comparte información personal, en la medida necesaria y sujeta a acuerdos contractuales de protección, con las siguientes categorías de destinatarios:',
        },
        {
          t: 'ul',
          items: [
            '**Proveedores de servicios** (procesadores de pago, verificación de identidad, infraestructura en la nube, mensajería, correo).',
            '**Agencias y socios operativos** en el flujo de entrega — en su rol de controladores o co-controladores independientes de sus propios datos.',
            '**Comerciantes y vendedores del Marketplace** para la ejecución del pedido — igualmente, controladores o co-controladores independientes de sus propios datos.',
            '**Tiendas Externas** en Casillero Tienda para ejecutar Compras Asistidas.',
            '**Transportistas Externos** en Casillero Envíos para el seguimiento del paquete.',
            '**Autoridades** cuando la ley aplicable así lo requiera.',
          ],
        },
        { t: 'p', text: 'Ver la Política de Privacidad (Sección 4) para el detalle completo.' },
      ],
    },
    {
      id: 'seccion-9',
      number: '9',
      title: 'Contacto y consultas',
      blocks: [
        {
          t: 'p',
          text: 'Para preguntas sobre este Aviso, sobre nuestras prácticas de privacidad, o para escalar una solicitud denegada:',
        },
        {
          t: 'ul',
          items: ['**Correo:** support@mienviocuba.com', '**Teléfono:** +1 (786) 519-1709'],
        },
        {
          t: 'p',
          text: 'Residentes de California que consideren que MEC ha violado sus derechos bajo la CCPA/CPRA pueden presentar una queja ante la California Privacy Protection Agency (cppa.ca.gov) o el Fiscal General de California (oag.ca.gov).',
        },
        {
          t: 'p',
          text: 'Residentes de otros estados: consulte a la oficina de protección al consumidor de su estado.',
        },
      ],
    },
    {
      id: 'seccion-10',
      number: '10',
      title: 'Modificaciones',
      blocks: [
        {
          t: 'p',
          text: 'MEC puede modificar este Aviso en cualquier momento. Los cambios materiales se notifican dentro de la Aplicación y en el sitio web, con al menos 30 días de anticipación cuando la ley lo requiera.',
        },
        {
          t: 'p',
          text: 'Este Aviso forma parte integrante de los Términos de Servicio (v1.0) y de la Política de Privacidad (v1.0), y se rige por el mismo derecho aplicable y foro descrito en aquellos, sin perjuicio de los derechos que las leyes estatales confieren a sus residentes.',
        },
      ],
    },
  ],
};
